import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getBrowserTimezone } from '@/utils/timezone';

/**
 * Timezone store state interface
 */
interface TimezoneState {
  /**
   * The currently selected IANA timezone (e.g., 'America/New_York')
   */
  timezone: string;

  /**
   * Set the user's preferred timezone
   */
  setTimezone: (timezone: string) => void;
}

/**
 * Local storage key for persisting timezone preference
 */
const STORAGE_KEY = 'user_timezone_preference';

/**
 * Zustand store for managing user's timezone preference
 *
 * Features:
 * - Persists timezone selection to localStorage
 * - Auto-detects browser timezone on first visit
 * - Provides global access to timezone state
 *
 * Usage:
 * ```tsx
 * import { useTimezoneStore } from '@/stores/timezoneStore';
 *
 * function MyComponent() {
 *   const { timezone, setTimezone } = useTimezoneStore();
 *
 *   return (
 *     <div>
 *       <p>Current timezone: {timezone}</p>
 *       <button onClick={() => setTimezone('America/New_York')}>
 *         Switch to Eastern Time
 *       </button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useTimezoneStore = create<TimezoneState>()(
  persist(
    set => ({
      // Default to browser-detected timezone
      timezone: getBrowserTimezone(),

      // Update timezone
      setTimezone: (timezone: string) => set({ timezone }),
    }),
    {
      name: STORAGE_KEY,
      // Only persist the timezone field
      partialize: state => ({ timezone: state.timezone }),
    }
  )
);
